import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
// toast is used by sub-tabs below
import { LogOut } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!localStorage.getItem("ldp_token")) { nav("/admin/login"); return; }
    refreshStats();
  }, []); // eslint-disable-line

  const refreshStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => {
    if (e.response?.status === 401) { localStorage.removeItem("ldp_token"); nav("/admin/login"); }
  });

  const logout = () => {
    localStorage.removeItem("ldp_token");
    localStorage.removeItem("ldp_user");
    nav("/admin/login");
  };

  return (
    <div className="py-12 px-6 md:px-10 max-w-7xl mx-auto" data-testid="admin-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-serif text-4xl mt-1">Dashboard</h1>
        </div>
        <button onClick={logout} className="border border-white/20 text-white/80 hover:text-white hover:border-[#D4AF37] px-5 py-2 text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="admin-logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Products", stats.products],
          ["Bookings", stats.bookings],
          ["House Calls", stats.house_calls],
          ["Articles", stats.articles],
          ["Newsletter", stats.newsletter],
          ["Pending Reviews", stats.testimonials_pending],
          ["Contact Msgs", stats.contacts],
          ["Paid Payments", stats.payments],
        ].map(([label, val], i) => (
          <div key={i} className="border border-white/10 p-5 bg-[#0A0A0A]" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="overline text-[#D4AF37]">{label}</div>
            <div className="font-serif text-3xl mt-2">{val ?? "—"}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products" className="mt-12">
        <TabsList className="bg-[#0A0A0A] border border-white/10 flex-wrap h-auto">
          {["analytics", "products", "bookings", "house-calls", "orders", "discounts", "custom-candles", "availability", "testimonials", "reviews", "articles", "newsletter", "contacts", "settings"].map((t) => (
            <TabsTrigger key={t} value={t} className="text-xs uppercase tracking-[0.18em] data-[state=active]:text-[#D4AF37]" data-testid={`tab-${t}`}>{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="analytics"><AnalyticsTab/></TabsContent>
        <TabsContent value="products"><ProductsTab onChange={refreshStats}/></TabsContent>
        <TabsContent value="bookings"><BookingsTab endpoint="/admin/bookings" testid="bookings"/></TabsContent>
        <TabsContent value="house-calls"><BookingsTab endpoint="/admin/house-calls" testid="house-calls" isHouseCall/></TabsContent>
        <TabsContent value="orders"><OrdersTab/></TabsContent>
        <TabsContent value="discounts"><DiscountsTab/></TabsContent>
        <TabsContent value="custom-candles"><CustomIntakesTab/></TabsContent>
        <TabsContent value="availability"><AvailabilityTab/></TabsContent>
        <TabsContent value="testimonials"><TestimonialsTab onChange={refreshStats}/></TabsContent>
        <TabsContent value="reviews"><ReviewRequestsTab/></TabsContent>
        <TabsContent value="articles"><ArticlesTab onChange={refreshStats}/></TabsContent>
        <TabsContent value="newsletter"><SimpleListTab endpoint="/admin/newsletter" columns={["email","name","created_at"]} testid="newsletter"/></TabsContent>
        <TabsContent value="contacts"><SimpleListTab endpoint="/admin/contacts" columns={["name","email","subject","message","created_at"]} testid="contacts"/></TabsContent>
        <TabsContent value="settings"><SettingsTab/></TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleListTab({ endpoint, columns, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)); }, [endpoint]);
  return (
    <div className="mt-6 overflow-x-auto border border-white/10" data-testid={`${testid}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-[#111] text-white/60">
          <tr>{columns.map((c) => <th key={c} className="text-left px-4 py-3 uppercase text-[10px] tracking-[0.18em]">{c.replace(/_/g, " ")}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5">{columns.map((c) => <td key={c} className="px-4 py-3 text-white/80">{String(r[c] ?? "—").slice(0, 80)}</td>)}</tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-white/40">No entries.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ProductsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name:"", category:"Fixed Candles", description:"", price:0, image_url:"", inventory:0, active:true });
  const load = () => api.get("/admin/products").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/products", { ...form, price: parseFloat(form.price), inventory: parseInt(form.inventory) || 0 }); toast.success("Product added"); setForm({ ...form, name:"", description:"", price:0, inventory:0, image_url:"" }); load(); onChange(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); onChange(); };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="lg:col-span-1 border border-white/10 p-6 space-y-4" data-testid="product-create-form">
        <h3 className="font-serif text-xl">New Product</h3>
        <input required placeholder="Name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="product-name-input"/>
        <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none">
          {["Fixed Candles","Spiritual Oils","Spiritual Baths","Soaps","Bracelets","Protection Products","Custom Products"].map(c => <option key={c} value={c} className="bg-black">{c}</option>)}
        </select>
        <textarea required placeholder="Description" rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <input required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <input type="number" placeholder="Inventory" value={form.inventory} onChange={(e)=>setForm({...form, inventory:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <input placeholder="Image URL" value={form.image_url} onChange={(e)=>setForm({...form, image_url:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <button type="submit" className="bg-[#8B0000] hover:bg-[#A50000] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] w-full" data-testid="product-create-submit">Add Product</button>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="border border-white/10 p-4 flex items-center justify-between gap-4" data-testid={`admin-product-${p.id}`}>
            <div>
              <div className="font-serif text-lg">{p.name}</div>
              <div className="text-xs text-white/50">{p.category} · ${Number(p.price).toFixed(2)} · {p.inventory} in stock</div>
            </div>
            <button onClick={() => del(p.id)} className="text-xs uppercase tracking-[0.2em] text-[#8B0000] hover:text-[#A50000]" data-testid={`product-delete-${p.id}`}>Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const moderate = async (id, approved) => { await api.put(`/admin/testimonials/${id}`, { approved }); load(); onChange(); };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); onChange(); };
  return (
    <div className="mt-6 space-y-3">
      {rows.map((t) => (
        <div key={t.id} className="border border-white/10 p-4 flex items-start justify-between gap-4" data-testid={`admin-testimonial-${t.id}`}>
          <div className="flex-1">
            <div className="font-serif text-lg">{t.name} <span className="text-xs text-white/50">{t.location}</span></div>
            <p className="text-sm text-white/70 mt-1 italic">"{t.content}"</p>
            <div className="text-[10px] uppercase tracking-[0.2em] mt-2 text-[#D4AF37]">{t.approved ? "Approved" : "Pending"}</div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => moderate(t.id, !t.approved)} className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37] px-3 py-1">{t.approved ? "Unapprove" : "Approve"}</button>
            <button onClick={() => del(t.id)} className="text-xs uppercase tracking-[0.2em] text-[#8B0000]">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArticlesTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title:"", slug:"", category:"Tarot Education", excerpt:"", content:"", image_url:"", published:true });
  const load = () => api.get("/admin/articles").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/articles", form); toast.success("Article saved"); setForm({ ...form, title:"", slug:"", excerpt:"", content:"", image_url:"" }); load(); onChange(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const del = async (id) => { await api.delete(`/admin/articles/${id}`); load(); onChange(); };
  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={submit} className="lg:col-span-1 border border-white/10 p-6 space-y-4">
        <h3 className="font-serif text-xl">New Article</h3>
        <input required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <input required placeholder="Slug" value={form.slug} onChange={(e)=>setForm({...form, slug:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none">
          {["Tarot Education","Spiritual Guidance","Spiritual Protection","Personal Growth","FAQs","Spiritual Insights","Community Education"].map(c => <option key={c} value={c} className="bg-black">{c}</option>)}
        </select>
        <input placeholder="Image URL" value={form.image_url} onChange={(e)=>setForm({...form, image_url:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <textarea required placeholder="Excerpt" rows={2} value={form.excerpt} onChange={(e)=>setForm({...form, excerpt:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <textarea required placeholder="Content" rows={6} value={form.content} onChange={(e)=>setForm({...form, content:e.target.value})} className="w-full bg-transparent border-b border-white/20 py-2 outline-none"/>
        <button type="submit" className="bg-[#8B0000] hover:bg-[#A50000] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] w-full">Add Article</button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {rows.map((a) => (
          <div key={a.id} className="border border-white/10 p-4 flex items-center justify-between gap-4">
            <div>
              <div className="font-serif text-lg">{a.title}</div>
              <div className="text-xs text-white/50">{a.category} · /{a.slug}</div>
            </div>
            <button onClick={() => del(a.id)} className="text-xs uppercase tracking-[0.2em] text-[#8B0000]">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [fives, setFives] = useState([]);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/testimonials").then((r) => setFives(r.data.filter((t) => t.approved && t.rating === 5)));
  }, []);
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(formatError(e)); } };
  if (!s) return <div className="mt-6 text-white/50">Loading…</div>;
  return (
    <div className="mt-6 space-y-6 max-w-2xl" data-testid="settings-tab">
      <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-autocomplete">
        <input type="checkbox" checked={!!s.auto_complete} onChange={(e) => setS({ ...s, auto_complete: e.target.checked })} />
        <span className="text-sm">Auto-mark bookings as <b>Completed</b> the day after their scheduled date (does not affect cancelled/refunded).</span>
      </label>
      <div>
        <label className="overline block mb-3">Featured Review of the Month</label>
        <select value={s.featured_testimonial_id || ""} onChange={(e) => setS({ ...s, featured_testimonial_id: e.target.value || null })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="settings-featured-select">
          <option value="" className="bg-black">Auto-rotate (latest 5-star approved)</option>
          {fives.map((t) => <option key={t.id} value={t.id} className="bg-black">{t.name}: {t.content.slice(0, 60)}…</option>)}
        </select>
      </div>

      <div className="border-t border-white/10 pt-6">
        <div className="overline mb-3 text-[#D4AF37]">Launch Schedule & Logo Auto-Swap</div>
        <p className="text-xs text-white/55 mb-4">
          The brand logo auto-swaps from the "Almost Open" announcement to a clean lion + the post-launch tagline
          ("Shielded By Fire. Guarded By Spirit.") on Soft Launch + Offset days.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="overline block mb-2">Soft Launch Date</label>
            <input type="date" value={s.soft_launch_date || ""} onChange={(e) => setS({ ...s, soft_launch_date: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="settings-soft-launch" />
          </div>
          <div>
            <label className="overline block mb-2">Grand Opening Date</label>
            <input type="date" value={s.grand_opening_date || ""} onChange={(e) => setS({ ...s, grand_opening_date: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="settings-grand-opening" />
          </div>
          <div>
            <label className="overline block mb-2">Logo Swap Offset (days)</label>
            <input type="number" min="0" value={s.logo_swap_offset_days ?? 180} onChange={(e) => setS({ ...s, logo_swap_offset_days: Number(e.target.value) })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="settings-swap-offset" />
          </div>
        </div>
      </div>

      <button onClick={save} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-8 py-3 text-xs uppercase tracking-[0.2em]" data-testid="settings-save">Save Settings</button>

      <EmailIntegrationPanel />
    </div>
  );
}

function EmailIntegrationPanel() {
  const [status, setStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/email-status").then((r) => {
      setStatus(r.data);
      if (r.data.admin_email && !testTo) setTestTo(r.data.admin_email);
    });
    // eslint-disable-next-line
  }, []);

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a destination email");
    setSending(true);
    try {
      await api.post("/admin/test-email", { to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  };

  if (!status) return null;

  return (
    <div className="border-t border-white/10 pt-6 mt-2" data-testid="email-integration-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="overline text-[#D4AF37]">Email Integration (Resend)</div>
        <span
          className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
            status.connected ? "bg-emerald-900/30 text-emerald-200" : "bg-white/10 text-white/50"
          }`}
          data-testid="email-status-badge"
        >
          {status.connected ? "Connected" : "Not Connected"}
        </span>
      </div>
      <p className="text-xs text-white/55 leading-relaxed mb-4">
        Powers booking confirmations, contact-form replies, newsletter welcomes, custom candle alerts,
        review requests, and 24-hour appointment reminders. API key is set via the{" "}
        <code className="text-white/80">RESEND_API_KEY</code> environment variable — never exposed to the browser.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-white/55 text-xs uppercase tracking-[0.18em] mb-1">Sender</div>
          <div className="text-white/85" data-testid="email-sender">{status.sender_email}</div>
        </div>
        <div>
          <div className="text-white/55 text-xs uppercase tracking-[0.18em] mb-1">Admin Notifications</div>
          <div className="text-white/85" data-testid="email-admin">{status.admin_email || "—"}</div>
        </div>
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="overline block mb-2">Send Test Email To</label>
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:border-[#D4AF37] outline-none"
            placeholder="founder@lyonsdenprotection.com"
            data-testid="email-test-to"
          />
        </div>
        <button
          onClick={sendTest}
          disabled={sending || !status.connected}
          className="bg-[#8B0000] hover:bg-[#A50000] disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 text-[11px] uppercase tracking-[0.2em]"
          data-testid="email-test-btn"
          title={!status.connected ? "Set RESEND_API_KEY in backend/.env first" : ""}
        >
          {sending ? "Sending…" : "Send Test"}
        </button>
      </div>
      {!status.connected && (
        <p className="mt-3 text-xs text-yellow-400/80">
          To enable emails, set <code className="text-yellow-200">RESEND_API_KEY</code> in <code className="text-yellow-200">backend/.env</code>{" "}
          and restart the backend.
        </p>
      )}
    </div>
  );
}

function ReviewRequestsTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/review-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const process = async () => {
    try { const { data } = await api.post("/admin/review-requests/process"); toast.success(`${data.created} new review request(s) generated`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const origin = window.location.origin;
  return (
    <div className="mt-6 space-y-4" data-testid="reviews-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-white/60">7 days after a booking is marked Completed, a review request is generated with a unique URL the customer can use to submit a testimonial.</p>
        <button onClick={process} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-5 py-2 text-xs uppercase tracking-[0.2em]" data-testid="reviews-process-btn">Run Review Request Job</button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="border border-white/10 p-4 flex items-start justify-between gap-4" data-testid={`review-req-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-serif text-lg">{r.name} · {r.service_name}</div>
              <div className="text-xs text-white/50 mt-1 truncate">{r.email}</div>
              <a href={`${origin}/review/${r.token}`} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] underline mt-2 inline-block break-all">{origin}/review/{r.token}</a>
            </div>
            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${r.submitted ? "border-green-600 text-green-400" : "border-white/30 text-white/60"}`}>{r.submitted ? "Submitted" : "Pending"}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="text-white/40 text-center py-10">No review requests yet. Run the job after marking bookings as Completed.</div>}
      </div>
    </div>
  );
}



function BookingsTab({ endpoint, testid, isHouseCall }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const patch = async (id, body) => {
    try { await api.patch(`${endpoint}/${id}`, body); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mt-6 space-y-3" data-testid={`${testid}-list`}>
      {rows.map((r) => (
        <div key={r.id} className="border border-white/10 p-4" data-testid={`${testid}-row-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-serif text-lg">{isHouseCall ? r.service_requested : r.service_name}</div>
              <div className="text-xs text-white/60 mt-1">
                {r.name} · {r.email} · {r.phone || "—"}
              </div>
              <div className="text-xs text-white/60 mt-1">
                {r.preferred_date} {r.preferred_time && `· ${r.preferred_time}`}
                {!isHouseCall && r.mode && ` · ${r.mode}`}
                {isHouseCall && ` · ${r.parish || "—"} · ${r.travel_tier}`}
              </div>
              {r.notes && <div className="text-xs text-white/50 mt-2 italic">"{r.notes}"</div>}
              {!isHouseCall && r.payment_plan === "deposit" && (
                <div className="text-xs text-[#D4AF37] mt-2" data-testid={`${testid}-deposit-${r.id}`}>
                  50% Deposit Plan · Paid ${Number(r.deposit_amount || 0).toFixed(2)} · Balance ${Number(r.balance_due || 0).toFixed(2)}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-2">
                <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
                  r.status === "confirmed" ? "border-[#D4AF37] text-[#D4AF37]" :
                  r.status === "completed" ? "border-green-600 text-green-400" :
                  r.status === "cancelled" ? "border-[#8B0000] text-[#A50000]" :
                  "border-white/30 text-white/60"
                }`}>{r.status}</span>
                {!isHouseCall && (
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
                    r.payment_status === "paid" ? "border-green-600 text-green-400" : "border-white/30 text-white/60"
                  }`}>{r.payment_status}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 justify-end">
                <select onChange={(e) => e.target.value && patch(r.id, { status: e.target.value })} value="" className="bg-[#0A0A0A] border border-white/15 text-[10px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`${testid}-status-${r.id}`}>
                  <option value="">Set Status…</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Approve</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancel</option>
                </select>
                {!isHouseCall && (
                  <select onChange={(e) => e.target.value && patch(r.id, { payment_status: e.target.value })} value="" className="bg-[#0A0A0A] border border-white/15 text-[10px] uppercase tracking-[0.15em] px-2 py-1" data-testid={`${testid}-payment-${r.id}`}>
                    <option value="">Set Payment…</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="text-white/40 text-center py-10">No entries.</div>}
    </div>
  );
}


const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const SERVICE_IDS = ["tarot-general","tarot-love","tarot-career","tarot-spiritual","tarot-custom","consult-30","consult-60","house-cleansing","protection-work","uncrossing","custom-spiritual"];

function AvailabilityTab() {
  const [av, setAv] = useState(null);
  const [blackoutInput, setBlackoutInput] = useState("");
  const [newTB, setNewTB] = useState({ date: "", start: "09:00", end: "10:00", reason: "" });
  const [newRB, setNewRB] = useState({ weekday: "0", start: "12:00", end: "13:00", reason: "" });

  useEffect(() => { api.get("/admin/availability").then((r) => setAv({ time_blocks: [], recurring_blocks: [], ...r.data })); }, []);
  if (!av) return <div className="mt-6 text-white/50">Loading…</div>;

  const setDay = (dayIdx, slots) => setAv({ ...av, weekly_hours: { ...av.weekly_hours, [dayIdx]: slots } });
  const setDuration = (sid, mins) => setAv({ ...av, service_durations: { ...av.service_durations, [sid]: parseInt(mins) || 30 } });
  const addBlackout = () => { if (blackoutInput && !av.blackout_dates.includes(blackoutInput)) { setAv({ ...av, blackout_dates: [...av.blackout_dates, blackoutInput].sort() }); setBlackoutInput(""); } };
  const removeBlackout = (d) => setAv({ ...av, blackout_dates: av.blackout_dates.filter(x => x !== d) });
  const addTB = () => { if (newTB.date && newTB.start && newTB.end) { setAv({ ...av, time_blocks: [...(av.time_blocks || []), { ...newTB }] }); setNewTB({ date: "", start: "09:00", end: "10:00", reason: "" }); } };
  const removeTB = (i) => setAv({ ...av, time_blocks: av.time_blocks.filter((_, idx) => idx !== i) });
  const addRB = () => { setAv({ ...av, recurring_blocks: [...(av.recurring_blocks || []), { ...newRB }] }); setNewRB({ weekday: "0", start: "12:00", end: "13:00", reason: "" }); };
  const removeRB = (i) => setAv({ ...av, recurring_blocks: av.recurring_blocks.filter((_, idx) => idx !== i) });
  const save = async () => {
    try { await api.put("/admin/availability", av); toast.success("Availability saved"); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="availability-tab">
      <div className="border border-white/10 p-6">
        <h3 className="font-serif text-xl mb-4">Weekly Hours</h3>
        <div className="space-y-3">
          {DAYS.map((d, i) => {
            const slots = av.weekly_hours?.[String(i)] || [];
            const slot = slots[0] || { start: "", end: "" };
            const enabled = slots.length > 0;
            return (
              <div key={i} className="flex items-center gap-3" data-testid={`avail-day-${i}`}>
                <label className="w-12 text-xs uppercase tracking-[0.18em] text-white/70">{d}</label>
                <input type="checkbox" checked={enabled} onChange={(e) => setDay(String(i), e.target.checked ? [{ start: "10:00", end: "17:00" }] : [])} />
                <input type="time" disabled={!enabled} value={slot.start} onChange={(e) => setDay(String(i), [{ ...slot, start: e.target.value }])} className="bg-transparent border-b border-white/20 py-1 text-sm disabled:opacity-30"/>
                <span className="text-white/40 text-sm">to</span>
                <input type="time" disabled={!enabled} value={slot.end} onChange={(e) => setDay(String(i), [{ ...slot, end: e.target.value }])} className="bg-transparent border-b border-white/20 py-1 text-sm disabled:opacity-30"/>
              </div>
            );
          })}
        </div>
        <div className="mt-6">
          <label className="overline block mb-2">Slot Step (minutes)</label>
          <input type="number" min={15} step={15} value={av.slot_minutes} onChange={(e) => setAv({ ...av, slot_minutes: parseInt(e.target.value) || 30 })} className="bg-transparent border-b border-white/20 py-2 outline-none w-32" data-testid="avail-slot-step"/>
        </div>
      </div>

      <div className="border border-white/10 p-6">
        <h3 className="font-serif text-xl mb-4">Service Durations (min)</h3>
        <div className="grid grid-cols-2 gap-3">
          {SERVICE_IDS.map((sid) => (
            <div key={sid} className="flex items-center gap-2 text-sm">
              <span className="flex-1 text-white/70 truncate">{sid}</span>
              <input type="number" min={15} step={15} value={av.service_durations?.[sid] || 30} onChange={(e) => setDuration(sid, e.target.value)} className="bg-transparent border-b border-white/20 py-1 w-20 outline-none text-right" data-testid={`avail-duration-${sid}`}/>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 p-6">
        <h3 className="font-serif text-xl mb-4">Full-Day Blackouts</h3>
        <div className="flex gap-2 mb-4">
          <input type="date" value={blackoutInput} onChange={(e) => setBlackoutInput(e.target.value)} className="bg-transparent border-b border-white/20 py-2 outline-none" data-testid="avail-blackout-input"/>
          <button type="button" onClick={addBlackout} className="border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black" data-testid="avail-blackout-add">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(av.blackout_dates || []).map((d) => (
            <span key={d} className="border border-white/15 px-3 py-1 text-sm flex items-center gap-2" data-testid={`avail-blackout-${d}`}>
              {d} <button onClick={() => removeBlackout(d)} className="text-[#8B0000] hover:text-[#A50000]">×</button>
            </span>
          ))}
          {(av.blackout_dates || []).length === 0 && <span className="text-white/40 text-sm">No blackout dates.</span>}
        </div>
      </div>

      <div className="border border-white/10 p-6">
        <h3 className="font-serif text-xl mb-4">Personal Unavailable Time (One-off)</h3>
        <p className="text-xs text-white/50 mb-4">Block specific hours on a specific date — for personal appointments, travel time, ritual work, etc.</p>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <input type="date" value={newTB.date} onChange={(e) => setNewTB({ ...newTB, date: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="tb-date"/>
          <input type="time" value={newTB.start} onChange={(e) => setNewTB({ ...newTB, start: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="tb-start"/>
          <span className="text-white/40">to</span>
          <input type="time" value={newTB.end} onChange={(e) => setNewTB({ ...newTB, end: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="tb-end"/>
          <input type="text" placeholder="Reason (optional)" value={newTB.reason} onChange={(e) => setNewTB({ ...newTB, reason: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm flex-1 min-w-[180px]" data-testid="tb-reason"/>
          <button type="button" onClick={addTB} className="border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black" data-testid="tb-add">Add Block</button>
        </div>
        <div className="space-y-2">
          {(av.time_blocks || []).map((tb, i) => (
            <div key={i} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm" data-testid={`tb-row-${i}`}>
              <span>{tb.date} · {tb.start}–{tb.end} {tb.reason && <span className="text-white/50 italic ml-2">"{tb.reason}"</span>}</span>
              <button onClick={() => removeTB(i)} className="text-[#8B0000] hover:text-[#A50000]">×</button>
            </div>
          ))}
          {(av.time_blocks || []).length === 0 && <span className="text-white/40 text-sm">No one-off blocks.</span>}
        </div>
      </div>

      <div className="border border-white/10 p-6 lg:col-span-2">
        <h3 className="font-serif text-xl mb-4">Recurring Weekly Blocks</h3>
        <p className="text-xs text-white/50 mb-4">e.g. lunch break every weekday, weekly ritual time, recurring personal appointment.</p>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <select value={newRB.weekday} onChange={(e) => setNewRB({ ...newRB, weekday: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="rb-weekday">
            {DAYS.map((d, i) => <option key={i} value={String(i)} className="bg-black">{d}</option>)}
          </select>
          <input type="time" value={newRB.start} onChange={(e) => setNewRB({ ...newRB, start: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="rb-start"/>
          <span className="text-white/40">to</span>
          <input type="time" value={newRB.end} onChange={(e) => setNewRB({ ...newRB, end: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm" data-testid="rb-end"/>
          <input type="text" placeholder="Reason (optional)" value={newRB.reason} onChange={(e) => setNewRB({ ...newRB, reason: e.target.value })} className="bg-transparent border-b border-white/20 py-2 outline-none text-sm flex-1 min-w-[180px]" data-testid="rb-reason"/>
          <button type="button" onClick={addRB} className="border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black" data-testid="rb-add">Add Recurring</button>
        </div>
        <div className="space-y-2">
          {(av.recurring_blocks || []).map((rb, i) => (
            <div key={i} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm" data-testid={`rb-row-${i}`}>
              <span>Every {DAYS[parseInt(rb.weekday)]} · {rb.start}–{rb.end} {rb.reason && <span className="text-white/50 italic ml-2">"{rb.reason}"</span>}</span>
              <button onClick={() => removeRB(i)} className="text-[#8B0000] hover:text-[#A50000]">×</button>
            </div>
          ))}
          {(av.recurring_blocks || []).length === 0 && <span className="text-white/40 text-sm">No recurring blocks.</span>}
        </div>
      </div>

      <div className="lg:col-span-2">
        <button onClick={save} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-8 py-3 text-xs uppercase tracking-[0.2em]" data-testid="avail-save-btn">Save Availability</button>
      </div>
    </div>
  );
}


const CANDLE_STATUSES = [
  { id: "pending_payment", label: "Pending Payment" },
  { id: "intake_received", label: "Intake Received" },
  { id: "in_preparation", label: "Candle In Preparation" },
  { id: "blessed", label: "Candle Blessed" },
  { id: "ready_to_ship", label: "Ready to Ship" },
  { id: "shipped", label: "Shipped" },
  { id: "completed", label: "Completed" },
];

const STATUS_LABEL = Object.fromEntries(CANDLE_STATUSES.map((s) => [s.id, s.label]));
const STATUS_COLORS = {
  pending_payment: "bg-white/10 text-white/60",
  intake_received: "bg-[#8B0000]/20 text-[#FFB4B4]",
  in_preparation: "bg-[#D4AF37]/20 text-[#D4AF37]",
  blessed: "bg-purple-900/30 text-purple-200",
  ready_to_ship: "bg-blue-900/30 text-blue-200",
  shipped: "bg-emerald-900/30 text-emerald-200",
  completed: "bg-white/20 text-white",
};

function CustomIntakesTab() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null); // intake_id selected for detail

  const load = () => api.get("/admin/custom-intakes").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6" data-testid="custom-intakes-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-[#D4AF37]">Custom Hand-Poured Candle Intakes</div>
          <p className="text-sm text-white/55 mt-1">Each row represents one bespoke candle ordered for a specific person and intention.</p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#D4AF37]" data-testid="intakes-refresh-btn">Refresh</button>
      </div>

      {rows.length === 0 ? (
        <div className="text-white/45 text-center py-20 border border-dashed border-white/10">No intakes yet. When a customer orders a Custom Hand-Poured Candle, their intake appears here.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="border border-white/10 bg-[#0A0A0A]" data-testid={`intake-row-${r.id}`}>
              <button
                onClick={() => setActive(active === r.id ? null : r.id)}
                className="w-full text-left flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 hover:bg-white/5 transition-colors"
                data-testid={`intake-toggle-${r.id}`}
              >
                <div>
                  <div className="font-serif text-lg">{r.full_name}</div>
                  <div className="text-xs text-white/55 mt-1">{r.email} · {new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="text-sm text-white/70 mt-2 max-w-xl truncate">{r.primary_intention}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${STATUS_COLORS[r.candle_status] || STATUS_COLORS.pending_payment}`}>
                    {STATUS_LABEL[r.candle_status] || r.candle_status}
                  </span>
                  <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${r.paid ? "bg-emerald-900/30 text-emerald-200" : "bg-white/10 text-white/50"}`}>
                    {r.paid ? "Paid" : "Unpaid"}
                  </span>
                </div>
              </button>
              {active === r.id && <IntakeDetail intake={r} onUpdated={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IntakeDetail({ intake, onUpdated }) {
  const [status, setStatus] = useState(intake.candle_status || "intake_received");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/custom-intakes/${intake.id}`, {
        candle_status: status,
        internal_note: note || null,
      });
      toast.success("Intake updated");
      setNote("");
      onUpdated();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, value }) => value ? (
    <div>
      <div className="overline text-[#D4AF37] mb-1">{label}</div>
      <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{value}</div>
    </div>
  ) : null;

  return (
    <div className="border-t border-white/10 px-5 py-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`intake-detail-${intake.id}`}>
      <div className="space-y-4">
        <F label="Primary Intention" value={intake.primary_intention} />
        <F label="Current Situation" value={intake.current_situation} />
        <F label="Desired Outcome" value={intake.desired_outcome} />
        <div className="grid grid-cols-2 gap-4 pt-2">
          <F label="Date of Birth" value={intake.date_of_birth} />
          <F label="Zodiac Sign" value={intake.zodiac_sign} />
        </div>
        <F label="People Involved" value={intake.others_involved} />
        <F label="Important Dates" value={intake.important_dates} />
        <F label="Additional Notes" value={intake.notes} />
      </div>

      <div className="space-y-5">
        <div>
          <label className="overline block mb-3">Candle Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full bg-transparent border border-white/15 py-2 px-3 text-white focus:border-[#D4AF37] outline-none"
            data-testid={`intake-status-select-${intake.id}`}
          >
            {CANDLE_STATUSES.map((s) => (
              <option key={s.id} value={s.id} className="bg-black">{s.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="overline block mb-3">Add Internal Note (visible only to admin)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="e.g. Started pour on 6/20, finished blessing on 6/22…"
            className="w-full bg-transparent border border-white/15 py-2 px-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
            data-testid={`intake-note-${intake.id}`}
          />
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="bg-[#8B0000] hover:bg-[#A50000] disabled:opacity-50 text-white px-6 py-3 text-[11px] uppercase tracking-[0.2em]"
          data-testid={`intake-save-${intake.id}`}
        >
          {saving ? "Saving…" : "Save"}
        </button>

        {Array.isArray(intake.internal_notes) && intake.internal_notes.length > 0 && (
          <div className="border-t border-white/10 pt-5">
            <div className="overline text-[#D4AF37] mb-3">Internal Note History</div>
            <ul className="space-y-3">
              {intake.internal_notes.slice().reverse().map((n, i) => (
                <li key={i} className="text-sm">
                  <div className="text-white/60 text-xs">{new Date(n.created_at).toLocaleString()}</div>
                  <div className="text-white/80 whitespace-pre-wrap">{n.note}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {intake.session_id && (
          <div className="text-xs text-white/40 pt-3 border-t border-white/10">
            Stripe Session: <code className="text-white/60">{intake.session_id}</code>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Orders Tab ----------
const FULFILLMENT_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "preparing", label: "Preparing" },
  { id: "shipped", label: "Shipped" },
  { id: "delivered", label: "Delivered" },
  { id: "refunded", label: "Refunded" },
];

function OrdersTab() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/orders").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6" data-testid="orders-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-[#D4AF37]">Shop Orders</div>
          <p className="text-sm text-white/55 mt-1">Multi-item candle orders paid via cart. Click a row to see full details and update fulfillment.</p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#D4AF37]" data-testid="orders-refresh-btn">Refresh</button>
      </div>

      {rows.length === 0 ? (
        <div className="text-white/45 text-center py-20 border border-dashed border-white/10">No orders yet.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((o) => (
            <div key={o.id} className="border border-white/10 bg-[#0A0A0A]" data-testid={`order-row-${o.id}`}>
              <button onClick={() => setActive(active === o.id ? null : o.id)} className="w-full text-left flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 hover:bg-white/5">
                <div>
                  <div className="font-serif text-lg">{o.shipping?.full_name}</div>
                  <div className="text-xs text-white/55 mt-1">{o.shipping?.email} · {new Date(o.created_at).toLocaleDateString()}</div>
                  <div className="text-sm text-white/70 mt-2">{o.items?.length} item{o.items?.length !== 1 && "s"} · ${o.total?.toFixed(2)}</div>
                </div>
                <span className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] bg-white/10 text-white/70">{o.fulfillment_status || "pending"}</span>
              </button>
              {active === o.id && <OrderDetail order={o} onUpdated={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order, onUpdated }) {
  const [status, setStatus] = useState(order.fulfillment_status || "pending");
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, { fulfillment_status: status, tracking_number: tracking });
      toast.success("Order updated");
      onUpdated();
    } catch (e) { toast.error(formatError(e)); } finally { setSaving(false); }
  };
  const a = order.shipping || {};
  return (
    <div className="border-t border-white/10 px-5 py-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`order-detail-${order.id}`}>
      <div>
        <div className="overline text-[#D4AF37] mb-3">Items</div>
        <ul className="space-y-2 text-sm">
          {(order.items || []).map((it, i) => (
            <li key={i} className="flex justify-between border-b border-white/5 pb-2">
              <span>{it.name} <span className="text-white/45">× {it.quantity}</span></span>
              <span className="text-white/80">${(it.line_total || 0).toFixed(2)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>${(order.subtotal || 0).toFixed(2)}</span></div>
          {order.discount_code && <div className="flex justify-between text-[#D4AF37]"><span>Discount ({order.discount_code})</span><span>-${(order.discount_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between border-t border-white/10 pt-2 mt-2 font-serif text-lg"><span>Total</span><span>${(order.total || 0).toFixed(2)}</span></div>
        </div>
        <div className="mt-6 text-sm">
          <div className="overline text-[#D4AF37] mb-2">Shipping To</div>
          <div className="leading-relaxed text-white/80">
            {a.full_name}<br/>{a.line1}{a.line2 && <>, {a.line2}</>}<br/>{a.city}, {a.state} {a.postal_code}<br/>{a.country}
            <div className="mt-2 text-xs text-white/55">{a.email} · {a.phone || "—"}</div>
          </div>
        </div>
      </div>
      <div className="space-y-5">
        <div>
          <label className="overline block mb-3">Fulfillment Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-transparent border border-white/15 py-2 px-3 text-white outline-none" data-testid={`order-status-select-${order.id}`}>
            {FULFILLMENT_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-black">{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-3">Tracking Number</label>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-full bg-transparent border border-white/15 py-2 px-3 text-white outline-none" data-testid={`order-tracking-${order.id}`} />
        </div>
        <button onClick={save} disabled={saving} className="bg-[#8B0000] hover:bg-[#A50000] disabled:opacity-50 text-white px-6 py-3 text-[11px] uppercase tracking-[0.2em]" data-testid={`order-save-${order.id}`}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}


// ---------- Discount Codes Tab ----------
function DiscountsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // existing code being edited, or "new" for new
  const blank = { code: "", discount_type: "percent", value: 10, min_spend: 0, max_uses: "", expires_at: "", description: "", active: true };
  const [draft, setDraft] = useState(blank);

  const load = () => api.get("/admin/discount-codes").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(blank); setEditing("new"); };
  const startEdit = (c) => {
    setDraft({
      code: c.code,
      discount_type: c.discount_type,
      value: c.value,
      min_spend: c.min_spend || 0,
      max_uses: c.max_uses ?? "",
      expires_at: c.expires_at || "",
      description: c.description || "",
      active: c.active,
    });
    setEditing(c.id);
  };
  const cancel = () => { setEditing(null); setDraft(blank); };

  const save = async () => {
    try {
      const payload = {
        ...draft,
        value: Number(draft.value),
        min_spend: Number(draft.min_spend || 0),
        max_uses: draft.max_uses === "" ? null : Number(draft.max_uses),
        expires_at: draft.expires_at || null,
      };
      if (editing === "new") {
        await api.post("/admin/discount-codes", payload);
        toast.success("Code created");
      } else {
        await api.patch(`/admin/discount-codes/${editing}`, payload);
        toast.success("Code updated");
      }
      cancel();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/discount-codes/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (c) => {
    if (!confirm(`Delete code ${c.code}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/discount-codes/${c.id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mt-6" data-testid="discounts-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline text-[#D4AF37]">Discount Codes</div>
          <p className="text-sm text-white/55 mt-1">Create promotional codes customers can apply at checkout. Codes are not case-sensitive.</p>
        </div>
        <button onClick={startNew} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-5 py-2 text-[10px] uppercase tracking-[0.2em]" data-testid="discount-new-btn">+ New Code</button>
      </div>

      {editing && (
        <div className="mb-8 border border-[#D4AF37]/30 bg-[#0A0A0A] p-6" data-testid="discount-editor">
          <div className="overline text-[#D4AF37] mb-4">{editing === "new" ? "New Code" : "Edit Code"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Code *</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} disabled={editing !== "new"} className="w-full bg-transparent border-b border-white/20 py-2 outline-none uppercase tracking-wider disabled:opacity-50" data-testid="discount-code-input" />
            </div>
            <div>
              <label className="overline block mb-2">Type *</label>
              <select value={draft.discount_type} onChange={(e) => setDraft({ ...draft, discount_type: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="discount-type-select">
                <option value="percent" className="bg-black">Percent (%)</option>
                <option value="fixed" className="bg-black">Fixed ($)</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Value *</label>
              <input type="number" step="0.01" value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" data-testid="discount-value-input" />
            </div>
            <div>
              <label className="overline block mb-2">Min Spend ($)</label>
              <input type="number" step="0.01" value={draft.min_spend} onChange={(e) => setDraft({ ...draft, min_spend: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Max Uses (blank = unlimited)</label>
              <input type="number" value={draft.max_uses} onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Expires</label>
              <input type="date" value={draft.expires_at || ""} onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Description</label>
              <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full bg-transparent border-b border-white/20 py-2 outline-none" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
              <span>Active</span>
            </label>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} className="bg-[#8B0000] hover:bg-[#A50000] text-white px-6 py-2.5 text-[10px] uppercase tracking-[0.2em]" data-testid="discount-save-btn">Save</button>
            <button onClick={cancel} className="border border-white/15 text-white/60 hover:text-white px-6 py-2.5 text-[10px] uppercase tracking-[0.2em]">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.id} className="border border-white/10 bg-[#0A0A0A] p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`discount-row-${c.code}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-serif text-xl tracking-wider">{c.code}</div>
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${c.active ? "bg-emerald-900/30 text-emerald-200" : "bg-white/10 text-white/50"}`}>{c.active ? "Active" : "Disabled"}</span>
              </div>
              <div className="text-sm text-white/65 mt-1">
                {c.discount_type === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                {c.min_spend > 0 && ` · min spend $${c.min_spend.toFixed(2)}`}
                {c.max_uses != null && ` · ${c.used_count || 0}/${c.max_uses} used`}
                {c.expires_at && ` · expires ${c.expires_at}`}
              </div>
              {c.description && <div className="text-xs text-white/45 mt-1">{c.description}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleActive(c)} className="text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-[#D4AF37]" data-testid={`discount-toggle-${c.code}`}>{c.active ? "Disable" : "Enable"}</button>
              <button onClick={() => startEdit(c)} className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:text-white" data-testid={`discount-edit-${c.code}`}>Edit</button>
              <button onClick={() => del(c)} className="text-[10px] uppercase tracking-[0.18em] text-white/45 hover:text-[#8B0000]" data-testid={`discount-delete-${c.code}`}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------- Analytics Tab ----------
const CANDLE_PIPELINE_ORDER = [
  { id: "pending_payment", label: "Pending Payment" },
  { id: "intake_received", label: "Intake Received" },
  { id: "in_preparation", label: "In Preparation" },
  { id: "blessed", label: "Blessed" },
  { id: "ready_to_ship", label: "Ready to Ship" },
  { id: "shipped", label: "Shipped" },
  { id: "completed", label: "Completed" },
];

function AnalyticsTab() {
  const [window, setW] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics?window=${window}`).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  }, [window]);

  if (!data) return <div className="mt-10 text-white/45 text-center py-20" data-testid="analytics-loading">Loading analytics…</div>;

  const T = data.totals;
  const pipelineRows = CANDLE_PIPELINE_ORDER.map((s) => ({ ...s, count: data.candle_pipeline?.[s.id] || 0 }));

  return (
    <div className="mt-6 space-y-10" data-testid="analytics-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="overline text-[#D4AF37]">Analytics Dashboard</div>
          <p className="text-sm text-white/55 mt-1">Revenue, top sellers, discount usage, and the Custom Hand-Poured pipeline.</p>
        </div>
        <div className="flex gap-2" data-testid="analytics-window-picker">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setW(d)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] border ${window === d ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/65 hover:border-[#D4AF37]/50"}`}
              data-testid={`analytics-window-${d}`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <Kpi label={`Revenue · ${window}d`} value={`$${T.revenue.toFixed(2)}`} testid="kpi-revenue" />
        <Kpi label="Shop Orders" value={T.order_count} sub={`$${T.order_revenue.toFixed(2)}`} testid="kpi-orders" />
        <Kpi label="Paid Bookings" value={T.booking_count} sub={`$${T.booking_revenue.toFixed(2)}`} testid="kpi-bookings" />
        <Kpi label="Custom Candles" value={T.candle_count} sub={`$${T.candle_revenue.toFixed(2)}`} testid="kpi-candles" />
      </div>

      <section data-testid="analytics-revenue-chart">
        <div className="overline text-[#D4AF37] mb-4">Revenue · Last {window} days</div>
        <div className="bg-[#0A0A0A] border border-white/10 p-4" style={{ height: 280 }}>
          {data.revenue_by_day.length === 0 ? (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">No revenue yet in this window.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_by_day}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={10} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ background: "#000", border: "1px solid rgba(212,175,55,0.4)" }} labelStyle={{ color: "#D4AF37" }} formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Area type="monotone" dataKey="revenue" stroke="#D4AF37" fill="url(#rev)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section data-testid="analytics-top-products">
          <div className="overline text-[#D4AF37] mb-4">Top Products · units sold</div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4" style={{ height: 320 }}>
            {data.top_products.length === 0 ? (
              <div className="h-full flex items-center justify-center text-white/40 text-sm">No products sold via cart yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" stroke="rgba(255,255,255,0.4)" fontSize={10} />
                  <YAxis dataKey="name" type="category" stroke="rgba(255,255,255,0.6)" fontSize={11} width={140} />
                  <Tooltip contentStyle={{ background: "#000", border: "1px solid rgba(212,175,55,0.4)" }} labelStyle={{ color: "#D4AF37" }} />
                  <Bar dataKey="units" fill="#8B0000" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section data-testid="analytics-candle-pipeline">
          <div className="overline text-[#D4AF37] mb-4">Custom Candle Pipeline · all-time</div>
          <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-3">
            {pipelineRows.map((s) => (
              <div key={s.id} className="flex items-center gap-4">
                <div className="w-36 text-sm text-white/75">{s.label}</div>
                <div className="flex-1 h-2 bg-white/5">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, s.count * 12)}%` }} />
                </div>
                <div className="w-10 text-right font-serif text-lg">{s.count}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section data-testid="analytics-discount-usage">
        <div className="overline text-[#D4AF37] mb-4">Discount Code Usage</div>
        <div className="bg-[#0A0A0A] border border-white/10">
          {data.discount_usage.length === 0 ? (
            <div className="text-center py-10 text-white/40 text-sm">No codes redeemed yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10">
                <th className="text-left p-3 font-normal overline text-[#D4AF37]">Code</th>
                <th className="text-left p-3 font-normal overline text-[#D4AF37]">Status</th>
                <th className="text-right p-3 font-normal overline text-[#D4AF37]">Times Used</th>
              </tr></thead>
              <tbody>
                {data.discount_usage.map((d) => (
                  <tr key={d.code} className="border-b border-white/5">
                    <td className="p-3 font-serif tracking-wider">{d.code}</td>
                    <td className="p-3"><span className={d.active ? "text-emerald-300" : "text-white/45"}>{d.active ? "Active" : "Disabled"}</span></td>
                    <td className="p-3 text-right font-serif text-lg">{d.used}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4" data-testid={testid}>
      <div className="overline text-white/55">{label}</div>
      <div className="font-serif text-3xl mt-2 text-white">{value}</div>
      {sub != null && <div className="text-xs text-[#D4AF37] mt-1">{sub}</div>}
    </div>
  );
}

